import { extractPayloadArray } from "@sd/lib/userDisplayMap";
import {
  formatPriorityLabel,
  priorityBucket,
} from "@sd/lib/ticketStreamUtils";

export type PriorityListingOption = {
  id: string;
  label: string;
};

/** Parse GET /service_desk/priority_listing/ into `{ id, label }` rows. */
export function parsePriorityListingOptions(
  payload: unknown
): PriorityListingOption[] {
  if (!payload) return [];

  let dataArray: unknown = payload;
  if (payload && typeof payload === "object") {
    const o = payload as Record<string, unknown>;
    if (Array.isArray(o.priorities)) dataArray = o.priorities;
    else if (Array.isArray(o.results)) dataArray = o.results;
    else if (Array.isArray(o.data)) dataArray = o.data;
  }

  const arr = extractPayloadArray(dataArray);
  const seen = new Set<string>();
  const out: PriorityListingOption[] = [];

  for (const raw of arr) {
    if (!raw || typeof raw !== "object") continue;
    const p = raw as Record<string, unknown>;
    const id = String(p.id ?? p.value ?? p.priority_id ?? "").trim();
    const label = formatPriorityLabel(
      p.priority ?? p.label ?? p.name ?? p.value ?? ""
    );
    if (!id || !label || label === "—" || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label });
  }

  return out;
}

export function buildPriorityLabelByIdMap(
  options: PriorityListingOption[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const opt of options) {
    map.set(opt.id, opt.label);
  }
  return map;
}

/** Alias used by hooks/components. */
export const buildPriorityIdToLabelMap = buildPriorityLabelByIdMap;

/** Map P1…P4 filter buckets → priority_listing UUID. */
export function buildPriorityIdByBucketFromListing(
  options: PriorityListingOption[]
): Record<string, string> {
  const byBucket: Record<string, string> = {};
  for (const opt of options) {
    const bucket = priorityBucket(opt.label);
    if (!byBucket[bucket]) {
      byBucket[bucket] = opt.id;
    }
  }
  return byBucket;
}

/** Resolve display label: priority_id from API first, then raw priority string. */
export function resolvePriorityDisplayLabel(
  priorityId: string | undefined,
  rawPriority: string | undefined,
  labelById: Map<string, string>
): string {
  if (priorityId) {
    const fromId = labelById.get(priorityId.trim());
    if (fromId) return fromId;
  }
  const raw = String(rawPriority ?? "").trim();
  if (raw && raw !== "—") {
    return formatPriorityLabel(raw);
  }
  return "—";
}

/** Alias used by ticket pages. */
export const resolvePriorityDisplay = resolvePriorityDisplayLabel;

/**
 * Admin + Super Agent panels: resolve human priority label from API.
 * Backend often sends `priority: null` and stores the value in `priority_id`.
 */
export function resolveTicketPriorityLabel(
  priorityId: string | undefined,
  rawPriority: string | undefined,
  labelById: Map<string, string>,
  options?: {
    slaPriorityById?: Record<string, string>;
    priorityLabelFromApi?: string;
  }
): string {
  const fromApiLabel = options?.priorityLabelFromApi?.trim();
  if (fromApiLabel) return formatPriorityLabel(fromApiLabel);

  const fromListing = resolvePriorityDisplayLabel(
    priorityId,
    rawPriority,
    labelById
  );
  if (fromListing !== "—") return fromListing;

  const id = priorityId?.trim();
  if (id && options?.slaPriorityById?.[id]) {
    return formatPriorityLabel(options.slaPriorityById[id]);
  }

  return "—";
}
